import { useCallback, useState } from 'react';

import type { OtpContext } from '../../../types';
import { ANALYTICS_KEYS, BUCK_EVENTS } from '../../../utils/analytics';
import { OtpError, OtpErrorType, sendOtpCode } from '../../../utils/authApi';
import type { AnalyticsHandler } from './useSignupFlow';

const RESEND_COOLDOWN_SECONDS = 60;

type UseMethodStepOptions = {
  apiBaseUrl: string;
  context?: OtpContext;
  onCodeSent: (email: string) => void;
  onAnalyticsEvent?: AnalyticsHandler;
};

type UseMethodStepReturn = {
  email: string;
  setEmail: (value: string) => void;
  emailError?: string;
  sendingCode: boolean;
  handleSendCode: () => Promise<void>;
};

export const useMethodStep = ({
  apiBaseUrl,
  context,
  onCodeSent,
  onAnalyticsEvent,
}: UseMethodStepOptions): UseMethodStepReturn => {
  const [email, setEmailValue] = useState('');
  const [emailError, setEmailError] = useState<string | undefined>();
  const [sendingCode, setSendingCode] = useState(false);

  const setEmail = useCallback(
    (value: string) => {
      setEmailValue(value);
      if (emailError) {
        setEmailError(undefined);
      }
    },
    [emailError]
  );

  const validateEmail = useCallback((value: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      setEmailError('Please enter a valid email address');
      return false;
    }
    setEmailError(undefined);
    return true;
  }, []);

  const handleSendCode = useCallback(async () => {
    if (!validateEmail(email)) {
      return;
    }

    setSendingCode(true);
    setEmailError(undefined);

    try {
      await sendOtpCode(apiBaseUrl, email, context);

      onAnalyticsEvent?.(BUCK_EVENTS.OTP_CODE_SENT, {
        [ANALYTICS_KEYS.LISTING_ID]: context?.listingId,
        [ANALYTICS_KEYS.PACKAGE_ID]: context?.packageId,
      });

      onCodeSent(email);
    } catch (error) {
      const otpError = error instanceof OtpError ? error : null;
      const errorMessage = 'Failed to send code. Try again.';

      onAnalyticsEvent?.(BUCK_EVENTS.OTP_ERROR, {
        [ANALYTICS_KEYS.ERROR_TYPE]: otpError?.type || 'unknown',
        [ANALYTICS_KEYS.ERROR_MESSAGE]: errorMessage,
        [ANALYTICS_KEYS.STEP]: 'send_code',
        [ANALYTICS_KEYS.LISTING_ID]: context?.listingId,
        [ANALYTICS_KEYS.PACKAGE_ID]: context?.packageId,
      });

      if (otpError?.type === OtpErrorType.RATE_LIMIT) {
        const cooldown = otpError.cooldownSeconds ?? RESEND_COOLDOWN_SECONDS;
        // For rate limit, include the cooldown info in the error message
        setEmailError(
          errorMessage.includes('seconds')
            ? errorMessage
            : `Please wait ${cooldown} seconds before trying again`
        );
      } else if (otpError?.type === OtpErrorType.NETWORK_ERROR) {
        setEmailError(`${errorMessage} Please try again.`);
      } else {
        setEmailError(errorMessage);
      }
    } finally {
      setSendingCode(false);
    }
  }, [apiBaseUrl, context, email, onAnalyticsEvent, onCodeSent, validateEmail]);

  return {
    email,
    setEmail,
    emailError,
    sendingCode,
    handleSendCode,
  };
};
